import type {
  CottageContactPageCreateForm,
  CottageContactPageCreateResponse,
} from '../entities/CottageContactPageCreate'

import type {
  CottageContactPagePricesDates,
  CottageContactPagePricesPeople,
} from '../entities/CottageContactPagePrices'

import type { CottageContactPage } from '../entities/CottageContactPage'
import { browserFetch as fetchEmailContact } from '~~/modules/api/rurality/v3/contacts/new'
import { getDataMining } from '~~/modules/integrations/datamining/composables/useDataMiningComposable'
import { type paramsType } from '~~/modules/api/rurality/v3/contacts/new/paramsSchema'

type responseType = {
  apiResponseSuccess: CottageContactPageCreateResponse | null
  apiResponseError: ApiResponseError | null
}

export function useCottageContactPageEmail() {
  const formatDateString = (dates: CottageContactPagePricesDates | null) => {
    if (dates && dates.start && dates.end) {
      const startString = toRuralityDate().displayDate(dates.start as Date)
      const endString = toRuralityDate().displayDate(dates.end as Date)

      return `${startString} - ${endString}`
    } else {
      return ''
    }
  }

  async function sendFormEmailData(params: {
    createForm: CottageContactPageCreateForm
    cottageContactPage: CottageContactPage
    dates: CottageContactPagePricesDates | null
    people: CottageContactPagePricesPeople | null
  }): Promise<responseType> {
    const dataMining = getDataMining()

    const responseCottagePage: responseType = {
      apiResponseSuccess: null,
      apiResponseError: null,
    }

    const showInputDate = formatDateString(params.dates)

    let finalMessage = ''

    if (showInputDate && params.people) {
      finalMessage = t(
        'views.cottage-contact.form.finalMessage.dateAndPeople',
        {
          date: showInputDate,
          people: params.people,
          message: params.createForm.message,
        },
      )
    } else if (showInputDate) {
      finalMessage = t('views.cottage-contact.form.finalMessage.date', {
        date: showInputDate,
        message: params.createForm.message,
      })
    } else if (params.people) {
      finalMessage = t('views.cottage-contact.form.finalMessage.people', {
        people: params.people,
        message: params.createForm.message,
      })
    } else {
      finalMessage = params.createForm.message
    }

    const queryParams: paramsType = {
      source: 'ESCAPADA_RURAL',
      cottageId: params.cottageContactPage.id,
      traveler: {
        name: params.createForm.name,
        email: params.createForm.email,
        phone: params.createForm.phone,
        allowComms: params.createForm.allowComms,
      },
      message: finalMessage,
      kpisData: dataMining,
      channel: 'EMAIL',
    }

    const contactEmailResponse = await fetchEmailContact(queryParams)

    if (contactEmailResponse?.success) {
      const cottageContactPageCreate: CottageContactPageCreateResponse = {
        id: contactEmailResponse.success.data.id,
        channel: contactEmailResponse.success.data.channel,
        message: contactEmailResponse.success.data.message,
        createdAt: contactEmailResponse.success.data.createdAt,
        owner: {
          id: contactEmailResponse.success.data.ownerId,
        },
        traveler: {
          id: contactEmailResponse.success.data.traveler.id,
          name: contactEmailResponse.success.data.traveler.name,
          email: contactEmailResponse.success.data.traveler.email,
          phone: contactEmailResponse.success.data.traveler.phone,
        },
        cottage: {
          id: contactEmailResponse.success.data.cottageId,
        },
      }
      responseCottagePage.apiResponseSuccess = cottageContactPageCreate
    } else if (contactEmailResponse.error) {
      responseCottagePage.apiResponseError =
        contactEmailResponse.error as ApiResponseError
    }

    return responseCottagePage
  }

  return {
    sendFormEmailData,
  }
}
